import { BD_DISTRICTS, BD_MAP_BOX } from './bdTravelMapData.js';

/**
 * The travel-map card, drawn on a canvas.
 *
 * ONE renderer serves the live preview and the download, so what the visitor sees
 * is exactly the file they get. The card is laid out in a fixed 1080 x 1500 box
 * (tall, because Bangladesh is, so the map can fill most of it); callers scale the context for sharper
 * output rather than re-laying anything out.
 */

export const CARD = { width: 1080, height: 1500 };
const PAD = 56;

/** Where the map sits on the card, and how big a map unit is there. */
export const MAP_PLACEMENT = (() => {
    const areaX = PAD;
    const areaY = 164;
    const areaW = CARD.width - PAD * 2;
    const areaH = 1206;
    const scale = Math.min(areaW / BD_MAP_BOX.width, areaH / BD_MAP_BOX.height);
    return {
        scale,
        x: areaX + (areaW - BD_MAP_BOX.width * scale) / 2,
        y: areaY + (areaH - BD_MAP_BOX.height * scale) / 2,
    };
})();

/** The card's colours: the Sohozkaj brand, on warm paper. */
export const BD_MAP_THEME = {
    bg: '#FFF8EC', base: '#EADFCB', fill: '#E68A00', onFill: '#FFFFFF',
    ink: '#1C1815', muted: '#8A7F73', line: '#FFF8EC', track: '#EADFCB',
};

const pathCache = new Map();
/** Path2D per district, built once: the same shapes are redrawn on every click. */
function pathFor(district) {
    let p = pathCache.get(district.id);
    if (!p) {
        p = new Path2D(district.d);
        pathCache.set(district.id, p);
    }
    return p;
}

function roundRect(ctx, x, y, w, h, r) {
    const radius = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + w, y, x + w, y + h, radius);
    ctx.arcTo(x + w, y + h, x, y + h, radius);
    ctx.arcTo(x, y + h, x, y, radius);
    ctx.arcTo(x, y, x + w, y, radius);
    ctx.closePath();
}

/** Shrink text until it fits, so a long name never runs into the count. */
function fitText(ctx, text, maxWidth, size, weight, family) {
    let px = size;
    ctx.font = `${weight} ${px}px ${family}`;
    while (px > 30 && ctx.measureText(text).width > maxWidth) {
        px -= 2;
        ctx.font = `${weight} ${px}px ${family}`;
    }
    return px;
}

/**
 * Draw the card.
 *
 * @param {CanvasRenderingContext2D} ctx already scaled so 1 unit = 1 card pixel
 * @param {object} o
 * @param {Set<string>} o.visited district ids
 * @param {string} o.title the heading (the visitor's name, or the default)
 * @param {string} o.kicker the small line above it
 * @param {boolean} o.showNames
 * @param {'bn'|'en'} o.lang
 * @param {string} o.fontFamily a CSS font-family list
 * @param {{ countText: string, totalText: string, percentText: string, partsText: string, brand: string, site: string }} o.labels
 * @param {number} o.divisionsVisited
 */
export function drawTravelMap(ctx, o) {
    const theme = BD_MAP_THEME;
    const family = o.fontFamily || 'sans-serif';
    const { width, height } = CARD;
    const count = o.visited.size;
    const total = BD_DISTRICTS.length;

    // Background
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, width, height);

    // Heading: the kicker, then the title
    const textX = PAD;
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillStyle = theme.muted;
    ctx.font = `600 24px ${family}`;
    ctx.fillText(o.kicker, textX, 80);

    // The count, top right: a big figure and the total beside it
    ctx.textAlign = 'right';
    ctx.fillStyle = theme.muted;
    ctx.font = `600 34px ${family}`;
    const totalText = o.labels.totalText;
    ctx.fillText(totalText, width - PAD, 138);
    const totalWidth = ctx.measureText(totalText).width;
    ctx.fillStyle = theme.fill;
    ctx.font = `800 92px ${family}`;
    ctx.fillText(o.labels.countText, width - PAD - totalWidth - 8, 138);
    const countWidth = ctx.measureText(o.labels.countText).width;

    ctx.textAlign = 'left';
    ctx.fillStyle = theme.ink;
    const maxTitleWidth = width - PAD - (PAD + totalWidth + countWidth + 40) - textX;
    fitText(ctx, o.title, Math.max(240, maxTitleWidth), 60, 800, family);
    ctx.fillText(o.title, textX, 140);

    // The map
    const { x: mx, y: my, scale: s } = MAP_PLACEMENT;
    ctx.save();
    ctx.translate(mx, my);
    ctx.scale(s, s);
    ctx.lineJoin = 'round';
    ctx.lineWidth = 1.4 / s;
    ctx.strokeStyle = theme.line;
    for (const district of BD_DISTRICTS) {
        const path = pathFor(district);
        ctx.fillStyle = o.visited.has(district.id) ? theme.fill : theme.base;
        ctx.fill(path);
        ctx.stroke(path);
    }
    ctx.restore();

    if (o.showNames) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `600 15.5px ${family}`;
        for (const district of BD_DISTRICTS) {
            const name = o.lang === 'en' ? district.en : district.bn;
            ctx.fillStyle = o.visited.has(district.id) ? theme.onFill : theme.ink;
            ctx.globalAlpha = o.visited.has(district.id) ? 1 : 0.7;
            ctx.fillText(name, mx + district.cx * s, my + district.cy * s);
        }
        ctx.globalAlpha = 1;
    }

    // Progress
    const barY = 1398;
    const barW = width - PAD * 2;
    roundRect(ctx, PAD, barY, barW, 12, 6);
    ctx.fillStyle = theme.track;
    ctx.fill();
    if (count > 0) {
        roundRect(ctx, PAD, barY, Math.max(12, (barW * count) / total), 12, 6);
        ctx.fillStyle = theme.fill;
        ctx.fill();
    }

    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
    ctx.fillStyle = theme.ink;
    ctx.font = `700 28px ${family}`;
    ctx.fillText(o.labels.percentText, PAD, 1450);
    ctx.textAlign = 'right';
    ctx.fillStyle = theme.muted;
    ctx.font = `600 24px ${family}`;
    ctx.fillText(o.labels.partsText, width - PAD, 1450);

    // Footer
    ctx.textAlign = 'left';
    ctx.fillStyle = theme.fill;
    ctx.font = `800 22px ${family}`;
    ctx.fillText(o.labels.brand, PAD, 1484);
    ctx.textAlign = 'right';
    ctx.fillStyle = theme.muted;
    ctx.font = `600 20px ${family}`;
    ctx.fillText(o.labels.site, width - PAD, 1484);
}

/** A fresh canvas holding the card at `scale` x resolution. */
export function renderTravelMapCanvas(options, scale = 2) {
    const canvas = document.createElement('canvas');
    canvas.width = CARD.width * scale;
    canvas.height = CARD.height * scale;
    const ctx = canvas.getContext('2d');
    ctx.scale(scale, scale);
    drawTravelMap(ctx, options);
    return canvas;
}
